import { useState, useEffect } from "react";
import {
  Calendar,
  Clock,
  Users,
  Video,
  Phone,
  MapPin,
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  PlayCircle,
  PauseCircle,
  CheckCircle,
  XCircle,
  MoreHorizontal,
  CalendarDays,
  Timer,
  User,
  Building2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { interviewAPI, recruitmentAPI, employeesAPI } from "@/lib/api";
import InterviewForm from "./InterviewForm";
import InterviewDetails from "./InterviewDetails";

export default function InterviewManagement() {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [interviewers, setInterviewers] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_items: 0,
    items_per_page: 10,
  });

  // Pagination helper variables
  const currentPage = pagination.current_page;
  const totalPages = pagination.total_pages;
  const setCurrentPage = (page) => {
    setPagination((prev) => ({ ...prev, current_page: page }));
  };

  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    fetchInterviews();
    fetchStats();
    fetchCandidates();
    fetchInterviewers();
  }, [searchTerm, statusFilter, typeFilter, pagination.current_page]);

  const fetchInterviews = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.current_page,
        limit: pagination.items_per_page,
        ...(searchTerm && { search: searchTerm }),
        ...(statusFilter !== "all" && { status: statusFilter }),
        ...(typeFilter !== "all" && { type: typeFilter }),
      };

      const response = await interviewAPI.getAll(params);
      setInterviews(response.data.data.interviews);
      setPagination(response.data.data.pagination);
    } catch (error) {
      console.error("Error fetching interviews:", error);
      toast({
        title: "Erreur",
        description: "Impossible de charger les entretiens",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await interviewAPI.getStats();
      setStats(response.data.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const fetchCandidates = async () => {
    try {
      const response = await recruitmentAPI.getCandidatures();
      setCandidates(response.data.data.applications || []);
      console.log("Fetched candidates:", response.data.data.applications || []);
    } catch (error) {
      console.error("Error fetching candidates:", error);
    }
  };

  const fetchInterviewers = async () => {
    try {
      // Fetch all active employees and filter for interview-eligible roles
      const response = await employeesAPI.getAll({
        statut: "actif", // Only active employees
        limit: 100, // Get more results to ensure we have all eligible interviewers
      });

      // Filter the results to only include admin, rh, and manager roles
      const eligibleInterviewers = (response.data.data.employees || []).filter(
        (employee) => ["admin", "rh", "manager"].includes(employee.role)
      );

      setInterviewers(eligibleInterviewers);
    } catch (error) {
      console.error("Error fetching interviewers:", error);
    }
  };

  const handleCreate = async (data) => {
    try {
      await interviewAPI.create(data);
      toast({
        title: "Succès",
        description: "Entretien créé avec succès",
      });
      setShowCreateModal(false);
      fetchInterviews();
      fetchStats();
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Erreur lors de la création de l'entretien",
        variant: "destructive",
      });
    }
  };

  const handleEdit = async (data) => {
    try {
      await interviewAPI.update(selectedInterview.id, data);
      toast({
        title: "Succès",
        description: "Entretien mis à jour avec succès",
      });
      setShowEditModal(false);
      setSelectedInterview(null);
      fetchInterviews();
      fetchStats();
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Erreur lors de la mise à jour de l'entretien",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async () => {
    try {
      await interviewAPI.delete(selectedInterview.id);
      toast({
        title: "Succès",
        description: "Entretien supprimé avec succès",
      });
      setShowDeleteModal(false);
      setSelectedInterview(null);
      fetchInterviews();
      fetchStats();
    } catch (error) {
      toast({
        title: "Erreur",
        description: "Erreur lors de la suppression de l'entretien",
        variant: "destructive",
      });
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      en_attente: "bg-orange-100 text-orange-800",
      confirme: "bg-blue-100 text-blue-800",
      en_cours: "bg-purple-100 text-purple-800",
      termine: "bg-green-100 text-green-800",
      reporte: "bg-yellow-100 text-yellow-800",
      annule: "bg-red-100 text-red-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  const getStatusLabel = (status) => {
    const labels = {
      en_attente: "En attente",
      confirme: "Confirmé",
      en_cours: "En cours",
      termine: "Terminé",
      reporte: "Reporté",
      annule: "Annulé",
    };
    return labels[status] || status;
  };

  const getStatusIcon = (status) => {
    const icons = {
      en_attente: <Clock className="h-4 w-4" />,
      confirme: <CheckCircle className="h-4 w-4" />,
      en_cours: <PlayCircle className="h-4 w-4" />,
      termine: <CheckCircle className="h-4 w-4" />,
      reporte: <PauseCircle className="h-4 w-4" />,
      annule: <XCircle className="h-4 w-4" />,
    };
    return icons[status] || <Clock className="h-4 w-4" />;
  };

  const getPlatformIcon = (type) => {
    const icons = {
      zoom: "🎥",
      google_meet: "📹",
      discord: "🎮",
      teams: "💼",
      telephonique: "📞",
      presentiel: "🏢",
      technique: "💻",
      visio: "📺",
    };
    return icons[type] || "📋";
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const canEdit = user?.role === "admin" || user?.role === "rh";

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Gestion des Entretiens
          </h1>
          <p className="text-gray-600">
            Planifiez et gérez vos entretiens avec diverses plateformes
          </p>
        </div>
        {canEdit && (
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nouvel Entretien
          </Button>
        )}
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Entretiens aujourd'hui
              </CardTitle>
              <CalendarDays className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.summary.today_interviews}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Cette semaine
              </CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.summary.week_interviews}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total entretiens
              </CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.summary.total_interviews}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Rechercher par candidat..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="en_attente">En attente</SelectItem>
                <SelectItem value="confirme">Confirmé</SelectItem>
                <SelectItem value="en_cours">En cours</SelectItem>
                <SelectItem value="termine">Terminé</SelectItem>
                <SelectItem value="reporte">Reporté</SelectItem>
                <SelectItem value="annule">Annulé</SelectItem>
              </SelectContent>
            </Select>
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="zoom">Zoom</SelectItem>
                <SelectItem value="google_meet">Google Meet</SelectItem>
                <SelectItem value="discord">Discord</SelectItem>
                <SelectItem value="teams">Microsoft Teams</SelectItem>
                <SelectItem value="telephonique">Téléphonique</SelectItem>
                <SelectItem value="presentiel">Présentiel</SelectItem>
                <SelectItem value="technique">Technique</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Interviews List */}
      <Card>
        <CardHeader>
          <CardTitle>Entretiens programmés</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : interviews.length === 0 ? (
            <div className="text-center py-8">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Aucun entretien trouvé
              </h3>
              <p className="text-gray-600">
                Aucun entretien ne correspond à vos critères de recherche.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {interviews.map((interview) => (
                <div
                  key={interview.id}
                  className="border rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-2xl">
                          {getPlatformIcon(interview.type_entretien)}
                        </span>
                        <div>
                          <h3 className="font-semibold text-lg">
                            {interview.candidature?.nom}{" "}
                            {interview.candidature?.prenom}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {interview.candidature?.offre?.titre} •{" "}
                            {interview.candidature?.offre?.departement?.nom}
                          </p>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
                        <div className="flex items-center gap-1">
                          <CalendarDays className="h-4 w-4" />
                          {formatDate(interview.date_entretien)}
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {formatTime(interview.date_entretien)}
                        </div>
                        {interview.duree && (
                          <div className="flex items-center gap-1">
                            <Timer className="h-4 w-4" />
                            {interview.duree} min
                          </div>
                        )}
                        {interview.intervieweur && (
                          <div className="flex items-center gap-1">
                            <User className="h-4 w-4" />
                            {interview.intervieweur.prenom}{" "}
                            {interview.intervieweur.nom}
                          </div>
                        )}
                      </div>

                      {interview.lieu && (
                        <div className="flex items-center gap-1 text-sm text-gray-600 mt-1">
                          <MapPin className="h-4 w-4" />
                          {interview.lieu}
                        </div>
                      )}

                      {interview.lien_reunion && (
                        <div className="mt-2">
                          <a
                            href={interview.lien_reunion}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm"
                          >
                            <Video className="h-4 w-4" />
                            Rejoindre la réunion
                          </a>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <Badge className={getStatusColor(interview.statut)}>
                        {getStatusIcon(interview.statut)}
                        <span className="ml-1">
                          {getStatusLabel(interview.statut)}
                        </span>
                      </Badge>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedInterview(interview);
                              setShowDetailsModal(true);
                            }}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Voir les détails
                          </DropdownMenuItem>
                          {canEdit && (
                            <>
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedInterview(interview);
                                  setShowEditModal(true);
                                }}
                              >
                                <Edit className="h-4 w-4 mr-2" />
                                Modifier
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedInterview(interview);
                                  setShowDeleteModal(true);
                                }}
                                className="text-red-600"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Supprimer
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="flex justify-center mt-6">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() =>
                    currentPage > 1 && setCurrentPage(currentPage - 1)
                  }
                  className={
                    currentPage === 1
                      ? "pointer-events-none opacity-50"
                      : "cursor-pointer"
                  }
                />
              </PaginationItem>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                (page) => (
                  <PaginationItem key={page}>
                    <PaginationLink
                      onClick={() => setCurrentPage(page)}
                      isActive={currentPage === page}
                      className="cursor-pointer"
                    >
                      {page}
                    </PaginationLink>
                  </PaginationItem>
                )
              )}

              <PaginationItem>
                <PaginationNext
                  onClick={() =>
                    currentPage < totalPages && setCurrentPage(currentPage + 1)
                  }
                  className={
                    currentPage === totalPages
                      ? "pointer-events-none opacity-50"
                      : "cursor-pointer"
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Programmer un nouvel entretien</DialogTitle>
              <DialogDescription>
                Sélectionnez une plateforme et planifiez votre entretien
              </DialogDescription>
            </DialogHeader>
            <InterviewForm
              onSubmit={handleCreate}
              onCancel={() => setShowCreateModal(false)}
              candidates={candidates}
              interviewers={interviewers}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedInterview && (
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Modifier l'entretien</DialogTitle>
              <DialogDescription>
                Modifiez les détails de l'entretien
              </DialogDescription>
            </DialogHeader>
            <InterviewForm
              interview={selectedInterview}
              onSubmit={handleEdit}
              onCancel={() => setShowEditModal(false)}
              candidates={[selectedInterview.candidature]}
              interviewers={interviewers}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedInterview && (
        <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <InterviewDetails
              interview={selectedInterview}
              onClose={() => setShowDetailsModal(false)}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Modal */}
      {showDeleteModal && selectedInterview && (
        <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Supprimer l'entretien</DialogTitle>
              <DialogDescription>
                Êtes-vous sûr de vouloir supprimer cet entretien ? Cette action
                est irréversible.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowDeleteModal(false)}
              >
                Annuler
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Supprimer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
